import { useState } from "react";
import { Badge, Box, Button, Divider, Group, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { PaperPlaneRightIcon, PaperclipIcon } from "@phosphor-icons/react";
import { showNotification } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnApi } from "../../apis";
import type { IReturnComment } from "../../apis/return";

// Lining figures so timestamps align.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

// §5 role badge — STAFF / FINANCE / MANAGER, consistent Mantine palette tokens.
const ROLE_COLOR: Record<string, string> = {
  STAFF  : "gray",
  FINANCE: "teal",
  MANAGER: "grape"
};

// Author name fallback when the id could not be resolved server-side.
const shortId = (id?: string | null) => (id ? `${id.slice(0, 8)}…` : "—");

interface Props {
  returnId: string;
}

// Renders the comment LIST + the add-comment composer. The section heading and
// bordered frame are provided by the Timeline tab's section wrapper.
export function ReturnCommentThread ({ returnId }: Props){
  const { t } = useTranslation("returns");
  const { data, mutate } = useSWR(
    ["returns.comments", returnId],
    () => ReturnApi.listComments(returnId),
    { revalidateOnFocus: false }
  );
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const comments = data?.comments ?? [];

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const created = await ReturnApi.addComment(returnId, { body: text });
      setBody("");
      // Optimistically append the created comment at the end (chronological),
      // then revalidate from the server so the cache reconciles.
      await mutate(
        (curr) => ({ comments: [...(curr?.comments ?? []), created] }),
        { revalidate: true }
      );
      showNotification({ color: "green", title: t("toast.success"), message: t("comment.added") });
    } catch (e) {
      showNotification({
        color  : "red",
        title  : t("toast.error"),
        message: (e as Error)?.message || t("comment.addError")
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md">
      <Stack gap="md">
        {comments.length === 0
          ? <Text c="dimmed" size="sm">{t("comment.empty")}</Text>
          : comments.map((c) => <CommentRow key={c.id} comment={c} />)}
      </Stack>

      <Divider />

      <Box>
        <Textarea
          placeholder={t("comment.placeholder")}
          autosize
          minRows={2}
          maxRows={6}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
        />
        <Group justify="flex-end" mt="xs">
          <Tooltip label={t("comment.attachSoon")} withArrow>
            <Button variant="default" leftSection={<PaperclipIcon size={16} />} disabled>
              {t("comment.attach")}
            </Button>
          </Tooltip>
          <Button
            leftSection={<PaperPlaneRightIcon size={16} />}
            onClick={submit}
            loading={busy}
            disabled={!body.trim()}
          >
            {t("comment.send")}
          </Button>
        </Group>
      </Box>
    </Stack>
  );
}

// §5 comment row: header = "AuthorName · timestamp" (left) + role Badge (right),
// a thin divider, then the body below.
function CommentRow ({ comment }: { comment: IReturnComment }){
  const { t } = useTranslation("returns");
  const color = ROLE_COLOR[comment.authorRole] ?? "gray";
  const label = t(`comment.author.${comment.authorRole}`, { defaultValue: comment.authorRole });
  return (
    <Box>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" fw={600}>
          {comment.authorName ?? shortId(comment.authorUserId)}
          <Text span size="sm" c="dimmed" fw={400} ml={6} style={TNUM}>
            · {new Date(comment.createdAt).toLocaleString("mn-MN")}
          </Text>
        </Text>
        <Badge size="xs" color={color} variant="light">{label}</Badge>
      </Group>
      <Divider my="xs" />
      <Text size="sm">{comment.body}</Text>
    </Box>
  );
}
